// src/pages/MeetingRoom/hooks/useMeetingSession.js
import { useEffect, useRef, useState, useCallback } from "react";
import useRealtimeWebRTC from "../../../hooks/useRealtimeWebRTC";
import useRealtimeTrainingFlow from "../../../hooks/useRealtimeTrainingFlow";
import usePracticeAssist from "./usePracticeAssist";
import { normalizeMeetingForRealtime } from "./realtimeMeetingAdapter";
import {
  buildOpeningPrompt,
  buildOpeningResponseInstructions,
  buildTrainingOpeningPrompt,
  buildTrainingOpeningResponseInstructions,
} from "@teleprompt/shared/realtime";

export default function useMeetingSession({
  convRef,
  originalMeetingConversationRef,
  setConversation,
  onEndProposed,
  applyInstructions,
  meetingData,
  trainingSession,
}) {
  const [sessionActive, setSessionActive] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const micVolumeRef = useRef(0); // 🔹 useRef instead of state
  const assistantVolumeRef = useRef(0);
  const [isConnecting, setIsConnecting] = useState(false);
  const [wsReady, setWsReady] = useState(false);
  const [trainingAnswerState, setTrainingAnswerState] = useState("idle");
  const [isAiSpeaking, setIsAiSpeaking] = useState(false);

  const endProposalHandlerRef = useRef(null);
  const smoothRef = useRef(0);
  const assistantSmoothRef = useRef(0);
  const sendEventRef = useRef(null);
  const realtimeReadyRef = useRef(false);
  const requestTrainingProgressEvaluationRef = useRef(null);
  const setCurrentTrainingExerciseRef = useRef(null);
  const resetTrainingFlowRef = useRef(null);
  const openerSentRef = useRef(false);
  const openerInFlightRef = useRef(false);
  const openerRetryTimeoutRef = useRef(null);
  const responseActiveRef = useRef(false);
  const pendingResponseCreateRef = useRef(null);
  const activeResponseLabelRef = useRef(null);
  const sessionReadyHandledRef = useRef(false);
  const trainingAnswerStateRef = useRef("idle");
  const trainingReadyTimeoutRef = useRef(null);
  const aiSpeakingRef = useRef(false);
  const aiSpeechQuietTimeoutRef = useRef(null);
  const isTrainingMode = Boolean(trainingSession);
  const realtimeMeeting = normalizeMeetingForRealtime(meetingData);
  const {
    enabled: assistModeEnabled,
    message: practiceAssistMessage,
    messageVersion: practiceAssistVersion,
    toggle: toggleAssistMode,
    capturePrompt: captureAssistPrompt,
    clearPrompt: clearAssistPrompt,
    requestForTurn: requestAssistForTurn,
    reset: resetPracticeAssist,
  } = usePracticeAssist({
    meetingId: meetingData?.id,
    isTrainingMode,
    conversationRef: convRef,
  });

  const updateTrainingAnswerState = useCallback((nextState) => {
    trainingAnswerStateRef.current = nextState;
    setTrainingAnswerState(nextState);
  }, []);

  const clearTrainingReadyTimeout = useCallback(() => {
    if (!trainingReadyTimeoutRef.current) return;
    clearTimeout(trainingReadyTimeoutRef.current);
    trainingReadyTimeoutRef.current = null;
  }, []);

  const scheduleTrainingAnswerReady = useCallback(() => {
    clearTrainingReadyTimeout();
    const checkForQuietPlayback = () => {
      if (!realtimeReadyRef.current) return;
      if (aiSpeakingRef.current || responseActiveRef.current) {
        trainingReadyTimeoutRef.current = setTimeout(checkForQuietPlayback, 250);
        return;
      }
      trainingReadyTimeoutRef.current = null;
      updateTrainingAnswerState("idle");
    };
    trainingReadyTimeoutRef.current = setTimeout(checkForQuietPlayback, 650);
  }, [clearTrainingReadyTimeout, updateTrainingAnswerState]);

  const setEndProposalHandler = useCallback((cb) => {
    endProposalHandlerRef.current = cb;
  }, []);

  // ✅ handle transcript additions
  const handleTranscript = useCallback(
    (msg, event) => {
      if (!msg?.trim()) {
        if (isTrainingMode) updateTrainingAnswerState("idle");
        return;
      }
      const text = msg.trim();
      const newEntry = { user: text };
      setConversation((prev) => [...prev, newEntry]);
      convRef.current.push(newEntry);
      if (isTrainingMode) {
        applyInstructions?.({
          meetingData,
          priorConvoRef: originalMeetingConversationRef || convRef,
          voiceId: meetingData?.aiVoice,
          sendEvent: sendEventRef.current,
          force: true,
        });
        const evaluationStarted = requestTrainingProgressEvaluationRef.current?.(text);
        if (!evaluationStarted) updateTrainingAnswerState("idle");
      } else {
        void requestAssistForTurn(text, event);
      }
    },
    [
      applyInstructions,
      convRef,
      isTrainingMode,
      meetingData,
      originalMeetingConversationRef,
      requestAssistForTurn,
      setConversation,
      updateTrainingAnswerState,
    ]
  );

  const handleResponse = useCallback(
    (res) => {
      if (!res?.trim()) return;
      const activeLabel = String(activeResponseLabelRef.current || "");
      if (
        isTrainingMode &&
        (activeLabel === "training-progress-evaluation" || activeLabel === "training-next-scenario")
      ) {
        return;
      }
      const text = res.trim();
      if (isTrainingMode && activeLabel === "training-follow-up") {
        setCurrentTrainingExerciseRef.current?.(text);
      }
      const newEntry = { ai: text };
      setConversation((prev) => [...prev, newEntry]);
      convRef.current.push(newEntry);
    },
    [setConversation, convRef, isTrainingMode]
  );

  const sendOpeningPrompt = useCallback((attempt = 1) => {
    if (openerSentRef.current || openerInFlightRef.current) return;
    if (!meetingData || !realtimeReadyRef.current) return;

    const normalizedMeeting = normalizeMeetingForRealtime(meetingData);
    const openingPrompt = trainingSession
      ? buildTrainingOpeningPrompt(normalizedMeeting, trainingSession)
      : buildOpeningPrompt(normalizedMeeting);
    const openingInstructions = trainingSession
      ? buildTrainingOpeningResponseInstructions(normalizedMeeting, openingPrompt)
      : buildOpeningResponseInstructions(normalizedMeeting, openingPrompt);

    openerInFlightRef.current = true;
    const sent = sendResponseCreateRef.current?.(
      {
        conversation: "none",
        input: [
          {
            type: "message",
            role: "system",
            content: [
              {
                type: "input_text",
                text: openingPrompt,
              },
            ],
          },
        ],
        instructions: openingInstructions,
      },
      "opening"
    );
    openerInFlightRef.current = false;

    if (sent) {
      openerSentRef.current = true;
      if (openerRetryTimeoutRef.current) {
        clearTimeout(openerRetryTimeoutRef.current);
        openerRetryTimeoutRef.current = null;
      }
      console.log("[Session] Opening prompt sent", { attempt });
      return;
    }

    if (attempt >= 8) {
      console.warn("[Session] Opening prompt was not accepted after retries");
      return;
    }

    if (openerRetryTimeoutRef.current) {
      clearTimeout(openerRetryTimeoutRef.current);
    }
    openerRetryTimeoutRef.current = setTimeout(() => {
      openerRetryTimeoutRef.current = null;
      sendOpeningPrompt(attempt + 1);
    }, 350);
  }, [meetingData, trainingSession]);

  const resetRealtimeSessionRefs = useCallback(() => {
    realtimeReadyRef.current = false;
    sessionReadyHandledRef.current = false;
    openerSentRef.current = false;
    openerInFlightRef.current = false;
    if (openerRetryTimeoutRef.current) {
      clearTimeout(openerRetryTimeoutRef.current);
      openerRetryTimeoutRef.current = null;
    }
    clearTrainingReadyTimeout();
    if (aiSpeechQuietTimeoutRef.current) {
      clearTimeout(aiSpeechQuietTimeoutRef.current);
      aiSpeechQuietTimeoutRef.current = null;
    }
    aiSpeakingRef.current = false;
    setIsAiSpeaking(false);
    updateTrainingAnswerState("idle");
    responseActiveRef.current = false;
    pendingResponseCreateRef.current = null;
    activeResponseLabelRef.current = null;
    resetTrainingFlowRef.current?.();
  }, [clearTrainingReadyTimeout, updateTrainingAnswerState]);

  // ✅ stable instruction application
  const handleSessionReady = useCallback(() => {
    if (sessionReadyHandledRef.current) return;
    sessionReadyHandledRef.current = true;
    setWsReady(true);
    setSessionActive(true);
    realtimeReadyRef.current = true;
    if (isTrainingMode) {
      resetTrainingFlowRef.current?.();
    }

    setTimeout(() => {
      if (applyInstructions && meetingData) {
        console.log("[Session] Applying instructions via onSessionReady");
        applyInstructions({
          meetingData,
          priorConvoRef: isTrainingMode
            ? originalMeetingConversationRef || convRef
            : convRef,
          voiceId: meetingData?.aiVoice,
          sendEvent: sendEventRef.current, // use ref (defined below)
        });
        setTimeout(() => sendOpeningPrompt(), 350);
      } else {
        console.warn("[Session] Skipping applyInstructions — missing data");
      }
    }, 250);
  }, [
    applyInstructions,
    meetingData,
    convRef,
    isTrainingMode,
    originalMeetingConversationRef,
    sendOpeningPrompt,
  ]);

  const handleClose = useCallback(() => {
    resetPracticeAssist();
    setSessionActive(false);
    setWsReady(false);
    resetRealtimeSessionRefs();
    assistantSmoothRef.current = 0;
    assistantVolumeRef.current = 0;
  }, [resetPracticeAssist, resetRealtimeSessionRefs]);

  const handleEndProposed = useCallback((args) => {
    endProposalHandlerRef.current?.(args);
  }, []);

  const handleRealtimeEvent = useCallback(
    (message) => {
      if (isTrainingMode) return;
      if (message?.type === "input_audio_buffer.speech_started") {
        captureAssistPrompt(message);
      } else if (message?.type === "conversation.item.input_audio_transcription.failed") {
        clearAssistPrompt(message);
      }
    },
    [captureAssistPrompt, clearAssistPrompt, isTrainingMode]
  );

  // ✅ mic volume: ref update only (no re-render)
  const handleVolume = useCallback(
    (rms) => {
      const amplified = Math.min(1, rms * 20);
      const emphasized = Math.pow(amplified, 0.9);
      const next = smoothRef.current * 0.85 + emphasized * 0.15;
      smoothRef.current = next;
      micVolumeRef.current = isMuted && !isTrainingMode ? 0 : next;
    },
    [isMuted, isTrainingMode]
  );

  const handleRemoteVolume = useCallback((rms) => {
    const amplified = Math.min(1, rms * 24);
    const emphasized = Math.pow(amplified, 0.9);
    const next = assistantSmoothRef.current * 0.82 + emphasized * 0.18;
    assistantSmoothRef.current = next;
    assistantVolumeRef.current = next;

    if (next > 0.02) {
      if (aiSpeechQuietTimeoutRef.current) {
        clearTimeout(aiSpeechQuietTimeoutRef.current);
        aiSpeechQuietTimeoutRef.current = null;
      }
      if (!aiSpeakingRef.current) {
        aiSpeakingRef.current = true;
        setIsAiSpeaking(true);
      }
      return;
    }

    if (aiSpeakingRef.current && !aiSpeechQuietTimeoutRef.current) {
      aiSpeechQuietTimeoutRef.current = setTimeout(() => {
        aiSpeechQuietTimeoutRef.current = null;
        if (assistantVolumeRef.current > 0.02) return;
        aiSpeakingRef.current = false;
        setIsAiSpeaking(false);
      }, 450);
    }
  }, []);

  const flushQueuedResponseCreate = useCallback(() => {
    if (responseActiveRef.current) return;
    const pending = pendingResponseCreateRef.current;
    if (!pending) return;

    pendingResponseCreateRef.current = null;
    const send = sendEventRef.current;
    if (typeof send !== "function") {
      pendingResponseCreateRef.current = pending;
      return;
    }

    const sent = send(pending.payload);
    if (sent) {
      responseActiveRef.current = true;
      activeResponseLabelRef.current = pending.label;
      console.log("[RealtimeWebRTC] queued response.create sent", { label: pending.label });
    } else {
      pendingResponseCreateRef.current = pending;
      console.log("[RealtimeWebRTC] queued response.create still waiting", { label: pending.label });
    }
  }, []);

  const sendResponseCreate = useCallback((response, label = "response") => {
    const payload = {
      type: "response.create",
      response,
    };

    if (responseActiveRef.current) {
      if (
        label === "training-progress-evaluation" &&
        pendingResponseCreateRef.current?.label === "training-progress-evaluation"
      ) {
        return false;
      }
      pendingResponseCreateRef.current = { label, payload };
      console.log("[RealtimeWebRTC] response.create queued", { label });
      return false;
    }

    const send = sendEventRef.current;
    if (typeof send !== "function") return false;
    const sent = send(payload);
    if (!sent) return false;
    responseActiveRef.current = true;
    activeResponseLabelRef.current = label;
    console.log("[RealtimeWebRTC] response.create sent", { label });
    return true;
  }, []);
  const sendResponseCreateRef = useRef(sendResponseCreate);

  const trainingFlow = useRealtimeTrainingFlow({
    isTrainingMode,
    meeting: realtimeMeeting,
    trainingPlan: trainingSession,
    readyRef: realtimeReadyRef,
    sendEvent: (payload) => {
      const send = sendEventRef.current;
      if (typeof send !== "function") return false;
      send(payload);
      return true;
    },
    sendResponseCreate,
  });
  useEffect(() => {
    requestTrainingProgressEvaluationRef.current = trainingFlow.requestTrainingProgressEvaluation;
    resetTrainingFlowRef.current = trainingFlow.resetTrainingFlow;
    setCurrentTrainingExerciseRef.current = trainingFlow.setCurrentTrainingExercise;
  }, [
    trainingFlow.requestTrainingProgressEvaluation,
    trainingFlow.resetTrainingFlow,
    trainingFlow.setCurrentTrainingExercise,
  ]);

  useEffect(() => {
    sendResponseCreateRef.current = sendResponseCreate;
  }, [sendResponseCreate]);

  // 🔹 Setup WebRTC handlers
  const {
    connect,
    disconnect,
    sendEvent,
    setMicEnabled,
    ensureMicPermission,
    getLocalStream,
    getRemoteStream,
  } = useRealtimeWebRTC({
    onTranscript: handleTranscript,
    onResponse: handleResponse,
    onSessionReady: handleSessionReady,
    onClose: handleClose,
    onEndProposed: handleEndProposed,
    onTrainingToolCall: trainingFlow.continueAfterTrainingToolCall,
    onResponseCreated: () => {
      responseActiveRef.current = true;
    },
    onResponseDone: () => {
      const completedLabel = activeResponseLabelRef.current;
      responseActiveRef.current = false;
      activeResponseLabelRef.current = null;
      if (
        isTrainingMode &&
        (completedLabel === "opening" || completedLabel === "training-follow-up")
      ) {
        scheduleTrainingAnswerReady();
      }
      setTimeout(flushQueuedResponseCreate, 0);
    },
    onRealtimeError: (error) => {
      if (error?.code === "response_cancel_not_active") return;
      if (error?.code === "conversation_already_has_active_response") {
        responseActiveRef.current = true;
        return;
      }
      responseActiveRef.current = false;
      activeResponseLabelRef.current = null;
      trainingFlow.clearEvaluationInFlight?.();
      if (isTrainingMode) {
        setMicEnabled(false);
        updateTrainingAnswerState("idle");
      }
      setTimeout(flushQueuedResponseCreate, 0);
    },
    onVolume: handleVolume,
    onRemoteVolume: handleRemoteVolume,
    onRealtimeEvent: handleRealtimeEvent,
  });

  // ✅ always keep sendEvent current for later use
  useEffect(() => {
    sendEventRef.current = sendEvent;
  }, [sendEvent]);

  const startTrainingAnswer = useCallback(() => {
    if (
      !isTrainingMode ||
      !sessionActive ||
      trainingAnswerStateRef.current !== "idle" ||
      responseActiveRef.current ||
      aiSpeakingRef.current ||
      trainingFlow.isEvaluationInFlight()
    ) {
      return false;
    }

    if (!sendEvent({ type: "input_audio_buffer.clear" })) return false;
    setMicEnabled(true);
    updateTrainingAnswerState("recording");
    return true;
  }, [
    isTrainingMode,
    sendEvent,
    sessionActive,
    setMicEnabled,
    trainingFlow,
    updateTrainingAnswerState,
  ]);

  const restartTrainingAnswer = useCallback(() => {
    if (!isTrainingMode || trainingAnswerStateRef.current !== "recording") return false;
    return sendEvent({ type: "input_audio_buffer.clear" });
  }, [isTrainingMode, sendEvent]);

  const submitTrainingAnswer = useCallback(() => {
    if (!isTrainingMode || trainingAnswerStateRef.current !== "recording") return false;
    setMicEnabled(false);
    updateTrainingAnswerState("processing");
    if (sendEvent({ type: "input_audio_buffer.commit" })) return true;

    updateTrainingAnswerState("idle");
    return false;
  }, [isTrainingMode, sendEvent, setMicEnabled, updateTrainingAnswerState]);

  const startRealtimeSession = useCallback(
    async (...args) => {
      resetPracticeAssist();
      resetRealtimeSessionRefs();
      setWsReady(false);
      updateTrainingAnswerState(isTrainingMode ? "processing" : "idle");
      return connect(...args);
    },
    [connect, isTrainingMode, resetPracticeAssist, resetRealtimeSessionRefs, updateTrainingAnswerState]
  );

  const stopRealtimeSession = useCallback(() => {
    resetPracticeAssist();
    resetRealtimeSessionRefs();
    setSessionActive(false);
    setWsReady(false);
    updateTrainingAnswerState("idle");
    disconnect();
  }, [disconnect, resetPracticeAssist, resetRealtimeSessionRefs, updateTrainingAnswerState]);

  // ✅ stable mic toggle (no extra renders)
  const toggleMic = useCallback(
    (muted) => {
      setIsMuted(muted);
      setMicEnabled(!muted);
      if (muted) {
        smoothRef.current = 0;
        micVolumeRef.current = 0;
      }
    },
    [setMicEnabled]
  );

  return {
    // Core session control
    sessionActive,
    setSessionActive,
    isMuted,
    setIsMuted,
    trainingAnswerState,
    isAiSpeaking,
    micVolumeRef, // 🔹 expose ref instead of state
    assistantVolumeRef,
    wsReady,
    isConnecting,
    setIsConnecting,

    // Realtime connection
    connect: startRealtimeSession,
    disconnect: stopRealtimeSession,
    sendEvent,
    setMicEnabled,
    ensureMicPermission,
    getLocalStream,
    getRemoteStream,
    toggleMic,
    startTrainingAnswer,
    submitTrainingAnswer,
    restartTrainingAnswer,
    assistModeEnabled,
    practiceAssistMessage,
    practiceAssistVersion,
    toggleAssistMode,
    setEndProposalHandler,
  };
}
